/** @jsx jsx */
import React, { useState, createContext, Dispatch, SetStateAction } from 'react';
import SidebarTemplate from 'components/templates/SidebarTemplate';
import FormContents from 'components/atoms/Form/FormContents';
import TabControl from 'components/atoms/TabControl';
import StaffInfoForm from 'components/organismos/master/general/staff/StaffInfoForm';
import AffiliationInfoForm from 'components/organismos/master/general/staff/AffiliationInfoForm';
import EmploymentInfoForm from 'components/organismos/master/general/staff/EmploymentInfoForm';
import SalaryInfoForm from 'components/organismos/master/general/staff/SalaryInfoForm';
import WorkPatternInfoForm from 'components/organismos/master/general/staff/WorkPatternInfoForm';
import LongTermCareAndNurse from 'components/organismos/master/general/staff/LongTermCareAndNurse';
import { isIPad13, isTablet } from "react-device-detect";
import BlockUI from 'components/molecules/BlockUi';
import useStaffEdit from './hooks';
import { TablItem } from 'components/atoms/TabBar';
import { css, jsx } from '@emotion/core';  
import { LoadingContextAdd, useInitialStaffAddPageState } from '../StaffAddPage/hooks';
import FormHeader from 'components/templates/FormHeader';

type SelectType = 'staff' | 'affiliation' | 'employment' | 'laborCost' | 'workPattern' | 'longTermCareandNurse';

const SelectType: { [key in SelectType]: SelectType } = {
  staff: 'staff',
  affiliation: 'affiliation',
  employment: 'employment',
  laborCost: 'laborCost',
  workPattern: 'workPattern',
  longTermCareandNurse: 'longTermCareandNurse',
};

type contextType = {
  setIsLoading: Dispatch<SetStateAction<boolean>>
};

export const LoadingContext = createContext<contextType>({} as contextType);

const StaffEditPage: React.FC = () => {
  const contextInitialState = useInitialStaffAddPageState();

  const {
    selectType, 
    setSelectType,
    isLoading, 
    setIsLoading
  } = contextInitialState;

  const {
    staffDomain, setStaffDomain,
    trialPeriodOptions, businessOptions,
    hqRoleOptions, shopRoleOptions, personalRoleOptions,
    executiveOptions, employmentOptions,
    orgTreeOptions,
    setOrgTargetDate,
    setEmpTargetDate,
    breakBusiness,
    roleOptions,
    holidayOptions,
    holidayTypeOptions
  } = useStaffEdit();
  
  const [tabItems, setTabItems] = useState<Array<TablItem>>([
    {
      value: SelectType.staff,
      display: 'スタッフ情報'
    },
    {
      value: SelectType.affiliation,
      display: '所属情報'
    },
    {
      value: SelectType.employment,
      display: '雇用情報'
    },
    // {
    //   value: SelectType.laborCost,
    //   display: '人件費情報',
    // },
    {
      value: SelectType.workPattern,
      display: '勤務パターン'
    },
    {
      value: SelectType.longTermCareandNurse,
      display: '介護・看護休暇情報'
    }
  ]);

  const isAuthorizedFromPeople = process.env.REACT_APP_IS_AUTHORIZED_FROM_PEOPLE == 'true' || false;
  
  return (
    <SidebarTemplate>
      <LoadingContextAdd.Provider value={contextInitialState}>
        <BlockUI blocking={isLoading}>
        <FormHeader title="スタッフ編集" />
        <FormContents customStyle={css({marginTop:'60px'})}>
          <TabControl
            items={tabItems}
            setValue={setSelectType as (arg: string) => void}
            currentValue={selectType}
            isFixed={isIPad13 || isTablet ? false : true}
            type={0}
            customStyle={css({ paddingTop:'15px' })}
          />

          {selectType === SelectType.staff
            && (
              <StaffInfoForm
                initialStaffDomain={staffDomain}
                setInitialStaffDomain={setStaffDomain}
                isEditMode={true}
                tabItems={tabItems}
                setTabItems={setTabItems}
                setSelectType={setSelectType}
                setOrgTargetDate={setOrgTargetDate}
                setEmpTargetDate={setEmpTargetDate}
                trialPeriodOptions={trialPeriodOptions}
                businessOptions={businessOptions}
                hqRoleOptions={hqRoleOptions}
                shopRoleOptions={shopRoleOptions}
                personalRoleOptions={personalRoleOptions}
                roleOptions={roleOptions}
                isDisabledInfo={isAuthorizedFromPeople}
                setIsLoading={setIsLoading}
              />
            )}

          {selectType === SelectType.affiliation
            && (
              <AffiliationInfoForm
                initialStaffDomain={staffDomain}
                setInitialStaffDomain={setStaffDomain}
                isEditMode={true}
                tabItems={tabItems}
                setTabItems={setTabItems}
                setSelectType={setSelectType}
                setOrgTargetDate={setOrgTargetDate}
                organizationOptions={orgTreeOptions}
                executiveOptions={executiveOptions}
                isDisabledInfo={isAuthorizedFromPeople}
                setIsLoading={setIsLoading}
              />
            )}

          {selectType === SelectType.employment
            && (
              <EmploymentInfoForm
                initialStaffDomain={staffDomain}
                setInitialStaffDomain={setStaffDomain}
                isEditMode={true}
                tabItems={tabItems}
                setTabItems={setTabItems}
                setSelectType={setSelectType}
                setEmpTargetDate={setEmpTargetDate}
                employmentOptions={employmentOptions}
                isDisabledInfo={isAuthorizedFromPeople}
                setIsLoading={setIsLoading}
              />
            )}

          {selectType === SelectType.laborCost
            && (
              <SalaryInfoForm
                initialStaffDomain={staffDomain}
                setInitialStaffDomain={setStaffDomain}
                isEditMode={true}
                tabItems={tabItems}
                setTabItems={setTabItems}
                setSelectType={setSelectType}
                businessOptions={businessOptions}
                setIsLoading={setIsLoading}
              />
            )}

          {selectType === SelectType.workPattern
            && (
              <WorkPatternInfoForm
                setInitialStaffDomain={setStaffDomain}
                initialStaffDomain={staffDomain}
                isEditMode={true}
                breakBusiness={breakBusiness}
                isDisabledInfo={isAuthorizedFromPeople}
                setIsLoading={setIsLoading}
              />
            )}

          {selectType === SelectType.longTermCareandNurse    
            && (
              <LongTermCareAndNurse
                initialStaffDomain={staffDomain}
                setInitialStaffDomain={setStaffDomain}
                isEditMode={true}
                tabItems={tabItems}
                setTabItems={setTabItems}
                setSelectType={setSelectType}
                setOrgTargetDate={setOrgTargetDate}
                organizationOptions={orgTreeOptions}
                executiveOptions={executiveOptions}
                isDisabledInfo={isAuthorizedFromPeople}
                setIsLoading={setIsLoading}
                holidayOptions={holidayOptions}
                holidayTypeOptions={holidayTypeOptions}
              />
            )}
        </FormContents>
      </BlockUI>
      </LoadingContextAdd.Provider>
    </SidebarTemplate>
  );
};

export default StaffEditPage;
